import { getContext, setContext } from "svelte"
import {
  fetchGuideCourse,
  setGuideLessonCompletion,
  type GuideCourse,
} from "../../../../../api/kehrblatt/learning-guide"

export type LearningProgressScope = { organizationId: string; userId: string }
export type LearningCompletionError = {
  lessonKey: string
  message: string
  versionConflict: boolean
}

const context = Symbol("kehrblatt-learning-progress")
const scopeKey = (scope: LearningProgressScope | null) =>
  scope ? JSON.stringify([scope.organizationId, scope.userId]) : null

/** One mounted organisation owns reads and confirmed completions for its authenticated user. */
export function createLearningProgressController(getScope: () => LearningProgressScope | null) {
  let activeKey = $state<string | null>(null)
  let response = $state.raw<GuideCourse | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let savingLessonKey = $state<string | null>(null)
  let completionError = $state<LearningCompletionError | null>(null)
  let readController: AbortController | null = null
  let writeController: AbortController | null = null
  let pendingRead: Promise<void> | null = null
  let disposed = false
  const ownsScope = (key: string | null) =>
    !disposed && key !== null && key === activeKey && key === scopeKey(getScope())

  function clearRequests() {
    readController?.abort()
    writeController?.abort()
    readController = null
    writeController = null
    pendingRead = null
  }

  async function loadCourse(): Promise<void> {
    const key = activeKey

    if (!ownsScope(key)) return

    if (pendingRead) return pendingRead

    const request = new AbortController()
    readController = request
    loading = true
    error = null
    const task = (async () => {
      try {
        const course = await fetchGuideCourse({ signal: request.signal })

        if (ownsScope(key) && !request.signal.aborted) {
          response = course
          completionError = null
        }
      } catch {
        if (ownsScope(key) && !request.signal.aborted)
          error = "Die Anleitungen konnten nicht geladen werden."
      } finally {
        if (readController === request) {
          readController = null
          pendingRead = null
          loading = false
        }
      }
    })()
    pendingRead = task

    return task
  }

  function activate(scope: LearningProgressScope | null) {
    const key = scopeKey(scope)

    if (disposed || key === activeKey) return

    clearRequests()
    activeKey = key
    response = null
    error = null
    completionError = null
    savingLessonKey = null
    loading = key !== null

    if (key) void loadCourse()
  }

  async function saveCompletion(
    lessonKey: string,
    completed: boolean,
    contentVersion: number,
  ): Promise<boolean> {
    const key = activeKey

    if (!ownsScope(key) || savingLessonKey !== null) return false

    const request = new AbortController()
    writeController = request
    savingLessonKey = lessonKey
    completionError = null
    try {
      const course = await setGuideLessonCompletion(
        lessonKey,
        { completed, content_version: contentVersion },
        { signal: request.signal },
      )

      if (!ownsScope(key) || request.signal.aborted) return false

      const lesson = course.lessons.find((candidate) => candidate.key === lessonKey)

      if (!lesson || lesson.content_version !== contentVersion)
        throw Object.assign(new Error("learning_guide_content_version_changed"), { status: 409 })

      if (lesson.completed !== completed) throw new Error("learning_guide_completion_not_confirmed")

      // A read begun before this write must not replace the confirmed completion.
      readController?.abort()
      readController = null
      pendingRead = null
      loading = false
      error = null
      response = course

      return true
    } catch (failure) {
      if (!ownsScope(key) || request.signal.aborted) return false

      const status =
        failure !== null && typeof failure === "object"
          ? ((failure as { status?: number; statusCode?: number }).status ??
            (failure as { statusCode?: number }).statusCode)
          : null
      const title =
        response?.lessons.find((lesson) => lesson.key === lessonKey)?.title ?? "Anleitung"
      completionError = {
        lessonKey,
        versionConflict: status === 409,
        message:
          status === 409
            ? `„${title}“ wurde inzwischen aktualisiert. Bitte laden Sie die Anleitung erneut.`
            : `Ihr Fortschritt für „${title}“ konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.`,
      }

      return false
    } finally {
      if (writeController === request) {
        writeController = null
        savingLessonKey = null
      }
    }
  }

  return {
    get course() {
      return ownsScope(activeKey) ? response : null
    },
    get loading() {
      return ownsScope(activeKey) ? loading : scopeKey(getScope()) !== null
    },
    get error() {
      return ownsScope(activeKey) ? error : null
    },
    get savingLessonKey() {
      return ownsScope(activeKey) ? savingLessonKey : null
    },
    get completionError() {
      return ownsScope(activeKey) ? completionError : null
    },
    activate,
    loadCourse,
    saveCompletion,
    dispose() {
      disposed = true
      clearRequests()
      response = null
    },
  }
}

export type LearningProgressController = ReturnType<typeof createLearningProgressController>

export function provideLearningProgress(controller: LearningProgressController) {
  return setContext(context, controller)
}

export function getLearningProgress(): LearningProgressController {
  const controller = getContext<LearningProgressController | undefined>(context)

  if (!controller) throw new Error("learning_progress_context_required")

  return controller
}
